import { reactive, markRaw } from 'vue';
import { api } from './api.js';

// Состояние раздела «Агент», общее для всех его показов.
//
// Раньше оно жило в самом компоненте и умирало вместе с ним: уходишь на
// вкладку с темой и обратно — выбранное место сбрасывается, статус на долю
// секунды показывает неправду, пока грузится заново, а открытый терминал
// закрывается сам. Всё это одна и та же причина, поэтому и лечится одним
// местом: состояние живёт дольше компонента.

const VIEW_KEY = 'hh.agent.view';

export const agent = reactive({
  // Пока не загрузились, статус не показываем вовсе: мигнуть неправдой хуже,
  // чем секунду показать «проверяю».
  loaded: false,
  place: 'local',
  view: localStorage.getItem(VIEW_KEY) === 'server' ? 'server' : 'local',
  hosts: [],
  tokens: [],
  options: { key: '', schemes: [], repo: '' },
  // Версия раннера, которая уедет на сервер при установке сейчас, и номер
  // релиза сайта — его же видно в GitLab.
  versionNow: '',
  releaseNow: '',
  state: null,
  fresh: null,
  error: '',
});

export function setView(view) {
  agent.view = view === 'server' ? 'server' : 'local';
  localStorage.setItem(VIEW_KEY, agent.view);
}

/** Полное состояние раздела. Дёргается редко: список серверов меняется руками. */
export async function refresh() {
  try {
    const [hosts, tokens, place, options, state] = await Promise.all([
      api.get('/api/agent-server/hosts'),
      api.get('/api/agent-server/tokens'),
      api.get('/api/agent-server/place'),
      api.get('/api/agent-server/options'),
      api.get('/api/runner/state').catch(() => null),
    ]);
    agent.hosts = hosts.rows;
    agent.versionNow = hosts.version_now || '';
    agent.releaseNow = hosts.release_now || '';
    agent.tokens = tokens.rows;
    agent.place = place.place;
    agent.options = options;
    agent.state = state;
    agent.error = '';
  } catch (e) {
    agent.error = e.message;
  } finally {
    agent.loaded = true;
  }
}

/** Только состояние раннера: его опрашиваем часто, это живой индикатор. */
export async function pollState() {
  const state = await api.get('/api/runner/state').catch(() => null);
  if (state) agent.state = state;
  agent.loaded = true;
}

// ── терминал ───────────────────────────────────────────────────────────────
//
// Живёт здесь по той же причине: уход на другую вкладку не должен рвать
// открытое соединение с сервером. Объекты xterm и WebSocket держим сырыми —
// реактивная обёртка вокруг терминала делает ввод заметно вязким.

export const term = reactive({
  hostId: 0,
  open: false,
  error: '',
  box: null,     // DOM-узел с терминалом: переносим его между показами
  xterm: null,
  ws: null,
  access: null,  // чем открыли: нужно, чтобы вернуться после обрыва
});

export function holdTerminal(hostId, { box, xterm, ws, access }) {
  term.hostId = hostId;
  term.open = true;
  term.box = markRaw(box);
  term.xterm = markRaw(xterm);
  term.ws = markRaw(ws);
  // Чем открыли — тем и переподключимся. Держим только в памяти вкладки и
  // только пока терминал открыт: переспрашивать пароль при обрыве значит
  // сделать кнопку «переподключиться» бессмысленной, а сохранённый доступ
  // сервера может не подойти — пароль могли сменить, за этим и пришли.
  if (access !== undefined) term.access = access ? markRaw(access) : null;
}

export function closeTerminal() {
  if (term.ws) { try { term.ws.close(); } catch { /* уже закрыт */ } }
  if (term.xterm) { try { term.xterm.dispose(); } catch { /* уже разобран */ } }
  term.hostId = 0;
  term.open = false;
  term.error = '';
  term.box = null;
  term.xterm = null;
  term.ws = null;
  term.access = null;
}
